import { useEffect } from "react";

import useTypedContext from "hooks/useTypedContext";
import { createPlainDataLayer, initPylonChat, setSegmentGroup } from "shared/Analytics";
import { SubscriptionContext } from "views/Account/SubscriptionWrapper";
import { AccountContext } from "views/AccountWrapper";
import usePylonAuthentication from "hooks/usePylonAuthentication";
import { isProductionFedRampEnvironment } from "utils/env";
import useTypedFlags from "hooks/useTypedFlags";

type ThirdPartyLibsWrapperProps = {
  children: React.ReactNode;
};

const ThirdPartyLibsWrapper = ({ children }: ThirdPartyLibsWrapperProps) => {
  const { accountId, viewer } = useTypedContext(AccountContext);
  const { tier, isTrial } = useTypedContext(SubscriptionContext);
  const { verifyPylonUser } = usePylonAuthentication();
  const isProductionFedRamp = isProductionFedRampEnvironment();
  const { pylonVerificationSetAccountExternalId, pylonVerificationSetContactExternalId } = useTypedFlags();

  useEffect(() => {
    if (!viewer?.email || !window.Pylon) {
      return;
    }

    if (!isProductionFedRamp) {
      verifyPylonUser()
        .then((response) => {
          initPylonChat({
            email: viewer.email,
            name: viewer.name,
            avatar: viewer.avatarURL,
            jwt: response.jwt,
            accountExternalID: pylonVerificationSetAccountExternalId
              ? response.account_external_id
              : undefined,
            contactExternalID: pylonVerificationSetContactExternalId
              ? response.contact_external_id
              : undefined,
          });
        })
        .catch(() => {
          // no-op: skip widget initialization if the verification request fails
        });
    } else {
      initPylonChat({
        email: viewer.email,
        name: viewer.name,
        avatar: viewer.avatarURL,
      });
    }
  }, [
    viewer,
    verifyPylonUser,
    isProductionFedRamp,
    pylonVerificationSetAccountExternalId,
    pylonVerificationSetContactExternalId,
  ]);

  useEffect(() => {
    // TODO: check if we still need this
    createPlainDataLayer({ account_status: isTrial ? "trial" : "paid" });
  }, [isTrial]);

  useEffect(() => {
    setSegmentGroup();
  }, [tier, isTrial, accountId, viewer?.id]);

  return <>{children}</>;
};

export default ThirdPartyLibsWrapper;
